/* eslint-disable react-hooks/exhaustive-deps */
import { Company } from 'src/core/models'
import { useTranslation } from 'next-i18next'
import { Row, Form, Button, Divider } from 'antd'
import React, { useEffect, useState } from 'react'
import { showNotification } from 'src/core/helpers'
import { FormLabels, CustomCol } from 'src/components'
import { useAppDispatch, useAppSelector } from 'src/app/hooks'
import { companyDetailsSections } from './company-details.sections'
import CompanyVatErrorModal from './CompanyVatErrorModal/CompanyVatErrorModal'
import {
  resetCompanyDetailState,
  resetEditCompanyState,
  countryListSelector,
  editCompanySelector,
  editCompanyDetails,
  userAuthenticatedState,
} from 'src/features'

const CompanyDetailsForm: React.FC<{ company: Company }> = ({ company }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const dispatch = useAppDispatch()
  const user = useAppSelector(userAuthenticatedState)
  const [isIntraCommunityOperator, setIsIntraCommunityOperator] = useState(false)
  const [isIntraCommunityDisabled, setIsIntraCommunityDisabled] = useState(false)
  const [showVatErrorModal, setShowVatErrorModal] = useState(false)
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const countryList = useAppSelector(countryListSelector)
  const {
    loading: loadingEdition,
    success: successEdition,
    error: errorVat
  } = useAppSelector(editCompanySelector)

  const handleCountryChange = (countryId: number) => {
    const country = countryList.find((country) => country.id === countryId)
    setIsIntraCommunityDisabled(!country.is_in_eu)
    setIsIntraCommunityOperator(country.is_in_eu ? isIntraCommunityOperator : false)
    form.resetFields(['is_intracommunity_operator'])
  }

  const handleIntraCommunityOperator = (value: boolean) => {
    setIsIntraCommunityOperator(value)
    form.resetFields(['is_intracommunity_operator'])

  }

  const onFinish = (values: any) => {
    const payload = { ...values, is_intracommunity_operator: isIntraCommunityOperator }
    dispatch(editCompanyDetails(company?.id, payload, false, user, countryList))
  }

  useEffect(() => {
    setIsIntraCommunityOperator(company?.is_intracommunity_operator)
    form.setFieldsValue({
      ['name']: company?.name,
      ['size']: company?.size,
      ['id_number']: company?.id_number,
      ['vat_number']: company?.vat_number,
      ['street']: company?.street,
      ['city']: company?.city,
      ['postal_code']: company?.postal_code,
      ['state']: company?.state,
      ['country']: company?.country?.id,
    })

    return (() => {
      dispatch(resetCompanyDetailState())
      dispatch(resetEditCompanyState())
    })
  }, [company])

  useEffect(() => {
    if (successEdition) {
      setShowVatErrorModal(false)
      setIsButtonDisabled(true)
      showNotification('success', t('settings.CompanySuccessUpdated'))
      dispatch(resetEditCompanyState())
    }
  }, [successEdition])

  useEffect(() => {
    if (errorVat) setShowVatErrorModal(true)
  }, [errorVat])

  const handleValidation = (e: any, key: string) => {
    if (key === 'is_intracommunity_operator') {
      setIsButtonDisabled(company?.is_intracommunity_operator === e)
    }
    else if (key === 'size') {
      setIsButtonDisabled(company?.size === e)
    }
    else if (key === 'country') {
      handleCountryChange(e)
      setIsButtonDisabled(company?.country?.id === e)
    }
    else if (company) setIsButtonDisabled(company[key] === e.target.value)
    else setIsButtonDisabled(false)
  }

  const companySections = companyDetailsSections(
    t, handleValidation, isIntraCommunityOperator,
    handleIntraCommunityOperator, isIntraCommunityDisabled
  )

  return <>
    <Form
      name='basic'
      onFinish={onFinish}
      autoComplete='off'
      form={form}
    >
      {/* Form Fields */}
      {companySections.map((section, idx) => <Row key={idx}>
        <CustomCol xs={section.xs1 || 24}>
          <FormLabels {...section.labels} />
        </CustomCol>
        <CustomCol xs={section.xs2 || 24}>
          {section.children ? (
            <section.component {...section.props}>{section.children}</section.component>
          ) : (
            <section.component {...section.props} />
          )}
        </CustomCol>
      </Row>)}

      <Divider />

      {/*Buttons Content */}
      <div className='float-right'>
        <Button
          type='primary'
          htmlType='submit'
          size='large'
          shape='round'
          loading={loadingEdition}
          disabled={isButtonDisabled}
        >
          {loadingEdition ? t('settings.UpdatingFields') : t('settings.UpdateCompany')}
        </Button>
      </div>
    </Form>

    <CompanyVatErrorModal
      isModalVisible={showVatErrorModal}
      vatNumber={form?.getFieldValue('vat_number')}
      countryName={countryList?.find((c: any) => c?.id === form?.getFieldValue('country'))?.name}
      handleCancel={() => {
        setShowVatErrorModal(false)
        form.setFieldsValue({ ['vat_number']: company?.vat_number })
        dispatch(resetEditCompanyState())
      }}
    />
  </>
}

export default CompanyDetailsForm
